<script lang="ts">
export interface CommandListOptionsProps<TOption, TValue> extends VirtualizerProps, ClassProps {
	options: ListOption<TOption, TValue>[];
}

export type CommandListOptionsEmit<TOption, TValue> = {
	select: [evt: { option: TOption; value: TValue }];
};
</script>

<script setup lang="ts" generic="TOption, TValue">
import { ListboxContent } from "reka-ui";
import { type ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import { useLocale } from "../../locale";
import { type ListOption, type OptionItem } from "../listbox/listbox";
import type { VirtualizerProps } from "../virtualizer/virtualizer";
import CommandItem from "./CommandItem.vue";
import CommandVirtualizer from "./CommandVirtualizer.vue";

const props = withDefaults(defineProps<CommandListOptionsProps<TOption, TValue>>(), {
	estimateHeight: 32,
	overscan: 5,
});
const emit = defineEmits<CommandListOptionsEmit<TOption, TValue>>();

defineSlots<{
	label?(slotProps: { label: string }): any;
	option?(slotProps: { option: TOption }): any;
	empty?(): any;
}>();

function handleSelect(option: OptionItem<TOption, TValue>) {
	emit("select", {
		option: option.option,
		value: option.value,
	});
}
const { t } = useLocale();

function resolveKey(option: ListOption<any, any>) {
	switch (option.type) {
		case "label":
			return option.label;
		case "separator":
			return "TODO";
		case "item":
			return option.value;
		case "custom-item":
			return option.value;
	}
}
</script>

<template>
	<ListboxContent
		:class="cn('max-h-72 scroll-py-1 overflow-y-auto overflow-x-hidden p-1', props.class)"
	>
		<div v-if="options.length === 0" class="py-3 text-center text-sm">
			<slot name="empty">
				{{ t("table.no-result") }}
			</slot>
		</div>
		<CommandVirtualizer
			v-else
			v-slot="{ option }"
			:virtual="props.virtual"
			:overscan="props.overscan"
			:estimateHeight="props.estimateHeight"
			:options="options"
		>
			<CommandItem :key="resolveKey(option)" :option="option" @select="handleSelect as any">
				<template #label="slotProps">
					<slot name="label" v-bind="slotProps" />
				</template>
				<template #option="slotProps">
					<slot name="option" v-bind="slotProps" />
				</template>
			</CommandItem>
		</CommandVirtualizer>
	</ListboxContent>
</template>
